import React, { useEffect, useMemo, useState, useCallback } from "react";
import Breadcrumbs from "../../../components/Breadcrumbs";
import Spinner from "../../../components/Spinner";
import AuthUser from "../../../components/AuthUser";
import { useTranslation } from "react-i18next";
import {
  Button,
  Drawer,
  Form,
  Input,
  Select,
  DatePicker,
  InputNumber,
} from "antd";
import { toast, ToastContainer } from "react-toastify";
import CandidaturaFilter from "../Administration/Users/CandidaturaFilter";
import CandidaturaTabs from "../Administration/Users/CandidaturaTabs";
import dayjs from "dayjs";

const { RangePicker } = DatePicker;

const ESTADOS = {
  1: "Recebida",
  2: "Triagem",
  3: "Entrevista",
  4: "Documentos",
  5: "Aprovada",
  6: "Reprovada",
};

const tabKeyFromEstado = (estado) => {
  switch (Number(estado)) {
    case 1:
      return "Recebidas";
    case 2:
      return "Triagem";
    case 3:
      return "Entrevista";
    case 4:
      return "Documentos";
    case 5:
      return "Aprovadas";
    case 6:
      return "Reprovadas";
    default:
      return "Recebidas";
  }
};

const Candidaturas = () => {
  const { t } = useTranslation();
  const { http } = AuthUser();

  const [hasFetched, setHasFetched] = useState(false);
  const [fetching, setFetching] = useState(false);
  const abortRef = React.useRef(null);

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]); // flat list
  const [meta, setMeta] = useState({ current_page: 1, per_page: 20, total: 0 });

  const [filters, setFilters] = useState({
    nome: "",
    provincia: "",
    cargo: "",
    estado: "ALL", // ALL | ATIVOS | REPROVADOS | 1..6
    dataRange: null, // [dayjs, dayjs]
  });

  const fetchCandidatos = async (page = 1, nextFilters = filters) => {
    try {
      // cancela request anterior
      if (abortRef.current) abortRef.current.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      setFetching(true);
      setLoading(true);

      const params = new URLSearchParams();
      params.set("page", String(page));
      params.set("perPage", "50");

      if (nextFilters.nome) params.set("nome", nextFilters.nome);
      if (nextFilters.provincia) params.set("provincia", nextFilters.provincia);
      if (nextFilters.cargo) params.set("cargoPretendido", nextFilters.cargo);
      if (nextFilters.estado) params.set("estado", nextFilters.estado);

      if (nextFilters.dataRange?.length == 2) {
        params.set("dataDe", nextFilters.dataRange[0].format("YYYY-MM-DD"));
        params.set("dataAte", nextFilters.dataRange[1].format("YYYY-MM-DD"));
      }

      const res = await http.get(`/candidatos?${params.toString()}`, {
        signal: controller.signal, // axios v1+ suporta
      });

      setRows(res.data.data || []);
      setMeta({
        current_page: res.data.current_page,
        per_page: res.data.per_page,
        total: res.data.total,
      });

      setHasFetched(true);
    } catch (e) {
      // ignora abort
      if (e?.name == "CanceledError" || e?.code == "ERR_CANCELED") return;
      console.error(e);
      toast.error("Erro ao carregar candidaturas.");
    } finally {
      setFetching(false);
      setLoading(false);
    }
  };

  // dropdowns
  const provincias = useMemo(
    () =>
      Array.from(new Set(rows.map((r) => r.provincia).filter(Boolean))).sort(),
    [rows]
  );
  const cargos = useMemo(
    () =>
      Array.from(
        new Set(rows.map((r) => r.cargoPretendido).filter(Boolean))
      ).sort(),
    [rows]
  );

  // agrupar em tabs
  const dataByTabs = useMemo(() => {
    const base = {
      Recebidas: [],
      Triagem: [],
      Entrevista: [],
      Documentos: [],
      Aprovadas: [],
      Reprovadas: [],
    };
    for (const r of rows) {
      const key = tabKeyFromEstado(r.estado);
      base[key].push({
        ...r,
        // para reaproveitar teu layout antigo
        status: r.statusLabel || ESTADOS[r.estado] || "—",
        score: r.score ?? 0, // se não tiveres score no backend, fica 0
        anexos: [], // vamos carregar no detalhe
      });
    }
    return base;
  }, [rows]);

  // === Ações reais do pipeline ===
  const updateEstado = async (candId, novoEstado) => {
    try {
      // 1. Atualização otimista no estado local
      setRows((prev) =>
        prev.map((r) =>
          r.id == candId
            ? { ...r, estado: novoEstado, statusLabel: ESTADOS[novoEstado] }
            : r
        )
      );

      const fd = new FormData();
      fd.append("estado", String(novoEstado));

      await http.post(`/candidatos/${candId}/estado`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      toast.success("Estado atualizado com sucesso.");
    } catch (e) {
      console.error(e);
      toast.error("Erro ao atualizar estado.");
      // fallback seguro
      fetchCandidatos(meta.current_page || 1);
    }
  };

  // === Drawer "Adicionar" (se quiseres usar o candidateStore já existente) ===
  const [addOpen, setAddOpen] = useState(false);
  const [form] = Form.useForm();

  const handleAdd = async (values) => {
    try {
      setLoading(true);

      const fd = new FormData();

      // exemplo mínimo (ajusta para TODOS os campos do teu candidateStore)
      fd.append("nome", values.nome);
      fd.append("email", values.email);
      fd.append("telefone", values.telefone);
      fd.append("provincia", values.provincia);
      fd.append("cargoPretendido", values.cargoPretendido);
      fd.append("disponibilidade", values.disponibilidade);
      fd.append("dataNasc", values.dataNasc?.format("YYYY-MM-DD"));
      fd.append("nacionalidade", values.nacionalidade || "Angolana");
      fd.append("nivelAcademico", values.nivelAcademico || "Ensino Médio");

      // anexos[] (multi)
      (values.anexos || []).forEach((file, idx) => {
        // Antd Upload costuma vir diferente; aqui deixei genérico
        fd.append(`anexos[Doc_${idx + 1}]`, file.originFileObj || file);
      });

      await http.post("/candidate/store", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      toast.success("Candidatura criada.");
      form.resetFields();
      setAddOpen(false);
      await fetchCandidatos(1);
    } catch (e) {
      console.error(e);
      console.log("Erro ao criar candidatura.");
    } finally {
      setLoading(false);
    }
  };

  if (loading)
    return (
      <>
        <Spinner />
        <ToastContainer />
      </>
    );

  return (
    <>
      <div className="page-wrapper">
        <div className="content container-fluid">
          <div className="d-flex align-items-center justify-content-between">
            <Breadcrumbs
              maintitle={t("Candidaturas")}
              title="Dashboard"
              subtitle={t("Candidaturas dos colaboradores")}
            />
            {/* <Button type="primary" onClick={() => setAddOpen(true)}>
              + Adicionar Candidatura
            </Button> */}
          </div>

          <div className="d-flex align-items-end justify-content-between gap-3">
            <div style={{ flex: 1 }}>
              <CandidaturaFilter
                filters={filters}
                onChange={(next) => setFilters(next)}
                cargos={cargos}
                provincias={provincias}
              />
            </div>

            <div className="pb-2">
              <Button
                type="primary"
                loading={fetching}
                onClick={() => fetchCandidatos(1, filters)}
              >
                {hasFetched ? "Atualizar" : "Carregar"}
              </Button>
            </div>
          </div>

          {!hasFetched ? (
            <div className="alert alert-info">
              Defina os filtros (opcional) e clique em <b>Carregar</b> para
              buscar candidaturas.
            </div>
          ) : (
            <CandidaturaTabs
              data={dataByTabs}
              onUpdateEstado={updateEstado}
              meta={meta}
              onRefresh={() => fetchCandidatos(meta.current_page || 1, filters)}
            />
          )}

        </div>
      </div>

      <Drawer
        title="Adicionar Candidatura"
        open={addOpen}
        width={720}
        onClose={() => setAddOpen(false)}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={handleAdd}>
          <div className="row">
            <div className="col-md-6">
              <Form.Item
                name="nome"
                label="Nome completo"
                rules={[{ required: true }]}
              >
                <Input className="form-control" />
              </Form.Item>
            </div>
            <div className="col-md-6">
              <Form.Item
                name="email"
                label="E-mail"
                rules={[{ type: "email", required: true }]}
              >
                <Input className="form-control" />
              </Form.Item>
            </div>
            <div className="col-md-6">
              <Form.Item
                name="telefone"
                label="Telefone"
                rules={[{ required: true }]}
              >
                <Input className="form-control" />
              </Form.Item>
            </div>

            <div className="col-md-6">
              <Form.Item
                name="dataNasc"
                label="Data de Nascimento"
                rules={[{ required: true }]}
              >
                <DatePicker className="w-100 form-control" />
              </Form.Item>
            </div>

            <div className="col-md-6">
              <Form.Item
                name="provincia"
                label="Província"
                rules={[{ required: true }]}
              >
                <Select
                  className="form-control"
                  options={provincias.map((p) => ({ label: p, value: p }))}
                />
              </Form.Item>
            </div>
            <div className="col-md-6">
              <Form.Item
                name="cargoPretendido"
                label="Cargo pretendido"
                rules={[{ required: true }]}
              >
                <Select
                  className="form-control"
                  options={cargos.map((c) => ({ label: c, value: c }))}
                />
              </Form.Item>
            </div>

            <div className="col-md-6">
              <Form.Item
                name="disponibilidade"
                label="Disponibilidade"
                rules={[{ required: true }]}
              >
                <Select
                  className="form-control"
                  options={["Imediata", "15 dias", "30 dias", "60 dias"].map(
                    (d) => ({ label: d, value: d })
                  )}
                />
              </Form.Item>
            </div>

            <div className="col-md-6">
              <Form.Item name="nivelAcademico" label="Nível Académico">
                <Input className="form-control" />
              </Form.Item>
            </div>
          </div>

          <div className="d-flex justify-content-end gap-2">
            <Button onClick={() => setAddOpen(false)}>Cancelar</Button>
            <Button type="primary" htmlType="submit">
              Guardar
            </Button>
          </div>
        </Form>
      </Drawer>

      <ToastContainer />
    </>
  );
};

export default Candidaturas;
